<script>
import moment from 'moment';
import swal from 'sweetalert';
import Apps from '@/core/Apps';
import { http } from '@/system/http';
import { size, pick } from 'lodash';

import CalendarType from '@/core/Calendar/CalendarType';
import Modal from '@/system/components/global/Modal';
import Iso8601Duration from '@/system/helpers/Iso8601Duration';
import TimePicker from '@/system/components/mixins/TimePicker';
import DatePicker from '@/system/components/global/form/DatePicker';
import ColorPicker from '@/system/components/global/form/ColorPicker';
import ExistingEntries from '@/system/components/mixins/ExistingEntries';
import ClientsSelectNew from '@/system/components/global/form/ClientsSelectNew';
import TextareaAutosize from '@/system/components/global/form/TextareaAutosize';
import ExistingEntriesWarning from 'Components/calendar/events/ExistingEntriesWarning';

export default {
    components: {
        Modal,
        DatePicker,
        ColorPicker,
        ClientsSelectNew,
        TextareaAutosize,
        ExistingEntriesWarning,
    },

    mixins: [
        TimePicker,
        ExistingEntries
    ],

    data() {
        return {
            showModal: false,
            booking: { practitioners: [] },
            chosen_client: false,
            chosen_contact: false,
            clientId: 0,
            contactId: 0,
            loading: false,
            repeatInterval: 'P1D',
            repeatUntilDate: moment().format('DD-MM-YYYY'),
            showRepeatOptions: false,
            minimize_practitioners: false,
            repeatOptions: Iso8601Duration,
            hasContacts: false,
            hasClients: false,
            addingEditBookingData: false,
        };
    },

    computed: {
        can_edit_clients() {
            return this.$can('edit-clients');
        },

        can_see_clients() {
            return this.$can('see-clients');
        },

        can_edit_apps() {
            return this.$can('edit-apps')
        },

        practitioners() {
            return this.$store.getters['practitioners/getPractitioners'];
        },


        calendars() {
            return this.$store.getters['calendars/getEditableCalendars']();
        },

        editMode() {
            return this.booking.id > 0;
        },

        chosen_calendar() {
            if (this.booking.calendarid > 0) {
                return this.$store.getters['calendars/getCalendarById'](this.booking.calendarid);
            }

            return false;
        },

        show_calendars_select() {
            return size(this.calendars) !== 1;
        },

        show_practitioners_select() {
            return this.practitioners.length > 0;
        },

        available_times() {
            let lastTime = this.owner.calendarendtime;

            if (moment(lastTime, 'HH:mm').isBefore(moment(this.booking.end_time, 'HH:mm'))) {
                lastTime = this.booking.end_time;
            }

            const availableTimes = this.get_available_times(this.owner.calendarstarttime, lastTime);
            const midnight = '00:00';
            const selectedTimes = [this.booking.start_time, this.booking.end_time];

            if (selectedTimes.includes(midnight) && !availableTimes.includes(midnight)) {
                availableTimes.unshift(midnight);
            }

            return availableTimes;
        },

        available_end_times () {
            const availableStartTimes = this.available_times;

            // only go ahead if the end date > start date, if not, just return all the available times
            if (this.isend_dateAfterStartDate) return availableStartTimes;

            // find index of this.booking.start_time and return the array after that index
            const findIndex = availableStartTimes && availableStartTimes.findIndex(time => time === this.booking.start_time);

            // remove all the times before the start time
            if (findIndex === availableStartTimes.length - 1) {
                return availableStartTimes.slice(findIndex);
            } else {
                return availableStartTimes.slice(findIndex + 1);
            }
        },

        owner() {
            return this.$store.state.auth.owner;
        },

        is_user_management_enabled() {
            return this.$store.getters['apps/isAppEnabled'](Apps.UserManagement)
                || this.$store.getters['apps/isAppEnabled'](Apps.Tenants);
        },

        practitionerCanSeeCalendar() {
            return (
                !this.is_user_management_enabled ||
                this.booking.practitioners.length === 0 ||
                this.$store.getters['practitioners/canUserSeeCalendar'](
                    this.booking.practitioners[0],
                    this.booking.calendarid,
                )
            );
        },

        isend_dateAfterStartDate () {
            const startDate =  moment(this.booking.start_date, 'DD/MM/YYYY');
            const end_date =  moment(this.booking.end_date, 'DD/MM/YYYY');
            return end_date > startDate;
        },

        // Other Payers is also known as Contacts and Payers
        isOtherPayersActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.OtherPayers);
        },

        requestUrl() {
            let url = '/app/contacts/bookings';

            if (this.editMode)
                url = `/app/contacts/bookings/${this.booking.id}`;

            if (this.showRepeatOptions)
                url = '/app/contacts/bookings/recurring';

            return url;
        },

        canCreateContact() {
            return this.can_edit_apps
                && this.can_edit_clients
        },

    },

    watch: {
        'booking.calendarid': function(val, oldVal) {
            if (val && val !== oldVal) {
                this.getExistingEntries()
            }
        },

        'booking.start_time': function() {
            // Don't override set end time when loading form.
            this.updateEndTime();
            this.getExistingEntries();
        },

        'booking.end_time': function() {
            this.getExistingEntries();
        },

        'booking.start_date': function(val, oldVal) {
            if (this.editMode) {
                this.booking.end_date = val;
            } else if (oldVal && oldVal === this.booking.end_date) {
                this.getExistingEntries();
            }
        },

        'booking.end_date': function(val) {
            if (!val) {
                this.booking.end_date = this.booking.start_date
            } else {
                this.getExistingEntries();
            }
        },

        'booking.contact_id': function(newVal) {
            if (newVal) {
                this.errors.remove('contact_id');
            }
        },

        'booking.clientid': function(newVal) {
            if (newVal?.length > 0) {
                this.errors.remove('clientid');
            }
        },

        showModal(val) {
            if (!val || !this.editMode) {
                this.reset();
            }
        },

        contactId(val) {
            if (val) {
                this.clientId = 0;
                this.checkContactHasClients(val);
            }
        },
    },

    mounted() {
        this.checkHasContacts();
        this.registerModalEventListeners();
        this.updateRecurrenceEventHandler();
        this.deleteRecurrenceEventHandler();
    },

    methods: {
        selectClient(client) {
            this.clientId = client?.id || 0;
            this.booking.clientid = this.clientId;
            this.chosen_client = client;
        },

        selectContact(contact) {
            this.contactId = contact?.id || 0;
            this.booking.contact_id = this.contactId;
            this.chosen_contact = contact;
        },

        async checkHasContacts() {
            if (!this.isOtherPayersActive) {
                return
            }

            const { data } = await http.get('/app/contacts/has-contacts');
            this.hasContacts = data.success;
        },

        async checkContactHasClients(contactId) {
            try {
                this.addingEditBookingData = true;
                const { data } = await http.get(`/app/contacts/${contactId}/has-clients`);
                this.hasClients = data.success;
            } finally {
                this.addingEditBookingData = false;
            }
        },

        getBooking(bookingId) {
            return new Promise((resolve, reject) => {
                http.get(`/app/booking/${bookingId}`)
                    .then(response => {
                        resolve(response.data)
                    })
                    .catch(errors => {
                        reject(errors)
                    })
            })
        },

        onBookingPractitionersInput(event) {
            this.booking.practitioners = JSON.parse(event.target.value);
            this.booking.behandler_booking_payment_guarantee_id = null
        },

        async getRecurringContactBookingDates() {
            this.loading = true;

            const contactBooking = this.getContactBookingRequestObject()

            try {
                const { data } = await http.post('/app/contacts/bookings/recurring/dates', contactBooking);

                swal({
                    title: this.trans('calendar.create_recurring_label'),
                    text: this.alertMessage(data),
                    showCancelButton: true,
                    html: true,
                    cancelButtonText: this.trans('global.cancel'),
                    showLoaderOnConfirm: true,
                    closeOnConfirm: false,
                }, () => {
                    swal.close();
                    this.submitBooking()
                });
            } catch ({ errorBag }) {
                this.$setErrorBag(errorBag);
            } finally {
                this.loading = false;
            }
        },

        getContactBookingRequestObject () {
            const contactBooking = {
                heading: this.booking.heading,
                start_date: this.booking.start_date,
                start_time: this.booking.start_time,
                end_date: this.booking.end_date,
                end_time: this.booking.end_time,
                contact_id: this.booking.contact_id,
                clientid: this.booking.clientid,
                calendarid: this.booking.calendarid,
                notes: this.booking.notes,
                color: this.booking.color,
                practitioners: this.booking.practitioners.map(practitioner => pick(practitioner, ['id'])),
            }

            if (this.showRepeatOptions) {
                contactBooking.until = this.repeatUntilDate
                contactBooking.recurring = this.repeatInterval
            }

            return contactBooking;
        },

        alertMessage({ contact_bookings }) {
            const message = this.trans('calendar.repeat_booking_confirmation', { number_of_bookings: contact_bookings.length });

            const dates = contact_bookings.map(cb => {
                return `<p>${cb}</p>`;
            }).join('');

            const confirmation = this.trans('calendar.repeat_booking_confirm')

            return `
                <div class="mb-5">${message}</div>
                <div class="mb-5">${dates}</div>
                <div>${confirmation}</div>
            `;
        },

        reset() {
            this.setDefaultContactBookingObject();
            this.selectContact(null);
            this.selectClient(null);
            this.showRepeatOptions = false;
            this.errors.clear();
        },

        setDefaultContactBookingObject() {
            let calendar = this.$store.getters['calendars/getDefaultCalendar']();

            let booking = {
                id: 0,
                start_date: moment().format('DD-MM-YYYY'),
                start_time: moment()
                    .startOf('hour')
                    .format('HH:mm'),
                end_date: moment().format('DD-MM-YYYY'),
                end_time: moment()
                    .add(1, 'hours')
                    .startOf('hour')
                    .format('HH:mm'),
                clientid: 0,
                contact_id: 0,
                calendarid: calendar ? calendar.id : null,
                notes: '',
                practitioners: [],
                heading: '',
            };

            let calendarEndTime = moment(this.owner.calendarendtime, 'HH:mm');
            let calendarStartTime = moment(this.owner.calendarstarttime, 'HH:mm');

            // set the booking dates to the nearest future within the calendar boundaries
            if (moment(booking.start_time, 'HH:mm').isBefore(calendarStartTime)) {
                booking.start_time = calendarStartTime.format('HH:mm');
                booking.end_time = calendarStartTime.add(1, 'hour').format('HH:mm');
            } else if (moment(booking.end_time, 'HH:mm').isAfter(calendarEndTime)) {
                booking.start_date = moment()
                    .add(1, 'day')
                    .format('DD-MM-YYYY');
                booking.end_date = moment()
                    .add(1, 'day')
                    .format('DD-MM-YYYY');
                booking.start_time = calendarStartTime.format('HH:mm');
                booking.end_time = calendarStartTime.add(1, 'hour').format('HH:mm');
            }

            this.booking = booking;

            if (this.showModal) {
                this.preselectPractitionerOnCalendar();
            }
        },

        showDeleteBooking(applyRecurringActionTo = null) {
            if (this.booking.recurring) {
                this.$event_bus.$emit('showRecurringContactBookingModal', this.booking, 'delete');
                return;
            }

            swal({
                title: this.trans('calendar.delete_booking_confirmation_title'),
                text: this.trans('calendar.delete_booking_confirmation'),
                showCancelButton: true,
                html: true,
                cancelButtonText: this.trans('global.cancel'),
                showLoaderOnConfirm: true,
                closeOnConfirm: true,
            }, async () => {
                await this.deleteContactBookingRequest(applyRecurringActionTo);
            })
        },

        async deleteContactBookingRequest(applyRecurringActionTo = null) {
            try {
                this.loading = true;

                const config = applyRecurringActionTo
                    ? { params: { action: applyRecurringActionTo } }
                    : null;

                const { data } = await http.delete('/app/contacts/bookings/' + this.booking.id, config);

                if (data.waiting_list_entries && data.waiting_list_entries.length > 0) {
                    swal(
                        {
                            title: data.success,
                            type: 'success',
                            text: this.trans('calendar.delete_booking_waiting_list'),
                            showCancelButton: true,
                            html: true,
                            confirmButtonText: this.trans('onboarding.yes'),
                            cancelButtonText: this.trans('global.cancel'),
                        },
                        () => {
                            const calendarId = data.deleted_booking ? data.deleted_booking.calendar_id : null;
                            const startDate = data.deleted_booking ? data.deleted_booking.start_date : false;
                            this.$event_bus.$emit('showWaitingListModal', calendarId, startDate);
                        },
                    );
                } else {
                    // eslint-disable-next-line no-undef
                    toastr.success(data.success);
                }

                this.$event_bus.$emit('recurringContactBookingAdjusted');
                this.showModal = false;
                await this.$nextTick();
                this.$event_bus.$emit('hideContactBookingModal');

            } catch ({ errorBag }) {
                if (errorBag) this.$setErrorBag(errorBag);
            } finally {
                this.loading = false;
            }
        },

        deleteRecurrenceEventHandler() {
            this.$event_bus.$on('deleteRecurringContactBookings', action => {
                this.deleteContactBookingRequest(action);
            });
        },

        async submitBooking(applyRecurringActionTo = null) {
            this.loading = true;

            let postData = this.getContactBookingRequestObject();

            postData.apply_recurring_action_to = applyRecurringActionTo;

            try {
                const { data } = await http.post(this.requestUrl, postData);
                this.showModal = false;
                this.loading = false;
                // eslint-disable-next-line no-undef
                toastr.success(data.success);

                this.$event_bus.$emit('calendarReload');

                if (this.editMode) {
                    this.$event_bus.$emit('showContactBookingModal', data.booking, false);

                    if (applyRecurringActionTo) {
                        this.$event_bus.$emit('recurringContactBookingAdjusted');
                    }
                }
            } catch ({ errorBag }) {
                this.$setErrorBag(errorBag);
                this.loading = false;
            }
        },

        submitForm() {
            this.errors.clear();

            if (this.editMode) {
                this.updateContactBooking();
                return;
            }

            if (this.showRepeatOptions) {
                this.getRecurringContactBookingDates();
                return;
            }

            this.submitBooking();
        },

        updateContactBooking () {
            if (this.booking.recurring) {
                this.$event_bus.$emit('showRecurringContactBookingModal', this.booking, 'update');
                return;
            }

            this.submitBooking();
        },

        updateEndTime() {
            //Set end time based on the duration of the chosen products
            if (this.booking.end_time && this.booking.end_time <= this.booking.start_time && !this.isend_dateAfterStartDate) {
                /*
                ** case when no offset is applied because of a preselected service
                ** If the start time is changed, and
                ** the booking end time is less than the start time, then
                ** reset the end time to the first value of the new available end times.
                */
                this.booking.end_time = this.available_end_times[0];
            }
        },

        getParameterByName(name) {
            return decodeURIComponent(
                window.location.search.replace(
                    new RegExp(
                        '^(?:.*[&\\?]' + encodeURIComponent(name).replace(/[\.\+\*]/g, '\\$&') + '(?:\\=([^&]*))?)?.*$',
                        'i',
                    ),
                    '$1',
                ),
            );
        },

        preselectPractitionerOnCalendar() {
            if (!this.is_user_management_enabled) {
                return
            }

            if (
                this.chosen_calendar.type === CalendarType.Practitioner &&
                this.chosen_calendar.practitioners.length > 0
            ) {
                const practitioner = this.$store.getters['practitioners/find'](this.chosen_calendar.practitioners[0]);
                if (practitioner) {
                    this.booking.practitioners = [ practitioner ];
                }
            } else {
                this.booking.practitioners = (this.booking.practitioners || []).map(p => {
                    return this.$store.getters['practitioners/find'](p);
                });
            }

            this.minimize_practitioners = !!(
                (this.chosen_calendar.practitioners || []).length &&
                this.booking.practitioners.length &&
                this.booking.practitioners[0].id === this.chosen_calendar.practitioners[0].id
            );
        },

        registerModalEventListeners() {
            this.$event_bus.$on('showCreateContactBooking', async ({ start, end, calendarId } = {}) => {
                this.showModal = true;
                await this.$nextTick();

                this.clientId = 0;
                this.contactId = 0;

                // If start and end is the same value the user did not click a timeframe in the calendar
                // and we set start and end time by default settings
                if (typeof start !== 'undefined' && typeof end !== 'undefined') {
                    this.booking.start_date = start.format('DD-MM-YYYY');
                    this.booking.start_time = start.format('HH:mm');
                    this.booking.end_date = end.format('DD-MM-YYYY');
                    this.booking.end_time = end.format('HH:mm');
                }

                if (calendarId) {
                    this.booking.calendarid = parseInt(calendarId);
                }
            });

            this.$event_bus.$on('editContactBooking', async (booking) => {
                this.showModal = true;

                this.booking = {
                    id: booking.id,
                    start_date: moment(booking.start).format('DD-MM-YYYY'),
                    start_time: moment(booking.start).format('HH:mm'),
                    end_date: moment(booking.end).format('DD-MM-YYYY'),
                    end_time: moment(booking.end).format('HH:mm'),
                    practitioners: (booking.practitioners || []).map(p => {
                        return this.$store.getters['practitioners/find'](p);
                    }),
                    contact_id: booking.contact?.id,
                    clientid: booking.client?.id,
                    calendarid: booking.calendar_id,
                    heading: booking.heading,
                    notes: booking.notes,
                    color: booking.color,
                    recurring: booking.recurring,
                }

                this.selectContact(booking.contact);
                await this.$nextTick();
                this.selectClient(booking.client);
            });
        },

        updateRecurrenceEventHandler() {
            this.$event_bus.$on('updateRecurringContactBookings', action => {
                this.submitBooking(action);
            });
        },

        // overriding the mixin method
        getSelectedStartDateTime() {
            if (!this.booking.start_date || !this.booking.start_time) {
                return null;
            }
            return moment(`${this.booking.start_date} ${this.booking.start_time}`, 'DD-MM-YYYY HH:mm');
        },

        // overriding the mixin method
        getSelectedEndDateTime() {
            if (!this.booking.end_date || !this.booking.end_time) {
                return null;
            }
            return moment(`${this.booking.end_date} ${this.booking.end_time}`, 'DD-MM-YYYY HH:mm');
        },
    },
};
</script>

<template>
    <modal
        id="create-contact-booking-modal"
        v-model="showModal"
        class="create-contact-booking"
    >
        <template #title>
            <h3 v-if="!editMode" class="panel-title">
                <i class="zmdi zmdi-account m-r-10"></i>{{ trans('apps.other_payers.contact_bookings.create') }}
            </h3>
            <h3 v-if="editMode" class="panel-title">
                <i class="zmdi zmdi-edit m-r-10"></i>{{ trans('calendar.edit_other_booking_heading') }}
            </h3>
        </template>

        <form id="create-contact-booking-form" ref="create-contact-booking-form" @submit.prevent="submitForm">
            <existing-entries-warning
                v-if="existingEntries.length > 0 && !showRepeatOptions"
                :existing-entries="existingEntries"
            />

            <div class="row">
                <div class="col-xs-12">
                    <div class="row">
                        <div
                            class="col-sm-3"
                            :class="{ 'col-xs-6': !editMode, 'col-xs-7': editMode }"
                        >
                            <div class="form-group" :class="{ 'has-error': errors.has('start_date') }">
                                <label for="start-date" class="control-label">
                                    {{ trans('global.start') }}
                                </label>
                                <date-picker
                                    id="start-date"
                                    v-model="booking.start_date"
                                    name="start-date"
                                ></date-picker>
                            </div>
                        </div>

                        <div
                            class="col-sm-2"
                            :class="{ 'col-xs-4': !editMode, 'col-xs-5': editMode }"
                        >
                            <div class="form-group" :class="{ 'has-error': errors.has('start_time') }">
                                <label for="start-time" class="control-label">&nbsp;</label>

                                <!-- v-model breaks this element on Safari. Could be fixed in future Safari/Vue releases -->
                                <select
                                    v-if="available_times && available_times.length > 0"
                                    id="start-time"
                                    class="form-control p-5"
                                    name="start_time"
                                    :value="booking.start_time"
                                    @input="booking.start_time = $event.target.value"
                                >
                                    <option
                                        v-for="(available_time) in available_times"
                                        :key="available_time"
                                        :value="available_time"
                                    >
                                        {{ available_time }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div
                            class="col-xs-2"
                            :class="{ 'col-sm-1': !editMode, 'col-sm-2 hidden-xs': editMode }"
                        >
                            <p
                                class="hidden-xs m-0 p-t-20 text-muted text-center"
                                style="padding-top:35px;"
                            >
                                <i class="zmdi zmdi-arrow-right"></i>
                            </p>
                        </div>

                        <div
                            class="col-sm-3"
                            :class="{ 'col-xs-6': !editMode, 'col-xs-7': editMode }"
                        >
                            <div class="form-group" :class="{ 'has-error': errors.has('end_date') }">
                                <label for="end-date" class="control-label">
                                    {{ trans('global.end') }}
                                </label>
                                <date-picker
                                    id="end-date"
                                    v-model="booking.end_date"
                                    name="end_date"
                                    :min_date="booking.start_date"
                                ></date-picker>
                            </div>
                        </div>

                        <div
                            class="col-sm-2"
                            :class="{ 'col-xs-4': !editMode, 'col-xs-5': editMode }"
                        >
                            <div class="form-group" :class="{ 'has-error': errors.has('end_time') }">
                                <label for="end-time" class="control-label">&nbsp;</label>

                                <!-- v-model breaks this element on Safari. Could be fixed in future Safari/Vue releases -->
                                <select
                                    id="end-time"
                                    class="form-control  p-5"
                                    name="end_time"
                                    :value="booking.end_time"
                                    @input="booking.end_time = $event.target.value"
                                >
                                    <option
                                        v-for="(available_time) in available_end_times"
                                        :key="available_time"
                                        :value="available_time"
                                    >
                                        {{ available_time }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div v-if="!editMode" class="col-xs-2 col-sm-1">
                            <div class="flex flex-col">
                                <label>&nbsp;</label>
                                <ep-button
                                    style="padding: 11px 0"
                                    variant="tertiary"
                                    start-icon="repeat"
                                    :title="trans('calendar.create_client_label_repeat_2')"
                                    @click.prevent="showRepeatOptions = !showRepeatOptions"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row"></div>

            <div v-if="!editMode" v-show="showRepeatOptions" class="row">
                <div class="col-xs-12">
                    <div class="form-group form-inline repeat_options_wrap">
                        {{ trans('calendar.create_client_label_repeat') }}

                        <!-- v-model breaks this element on Safari. Could be fixed in future Safari/Vue releases -->
                        <select
                            id="repeat_interval"
                            v-model="repeatInterval"
                            name="repeat_interval"
                            class="form-control m-l-10 m-r-10"
                        >
                            <option
                                v-for="option in repeatOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ trans(option.label) }}
                            </option>
                        </select>

                        {{ trans('calendar.create_client_repeat_untill') }}

                        <div class="form-group" :class="{ 'has-error' : errors.has('until') }">
                            <date-picker
                                v-model="repeatUntilDate"
                                name="repeatUntilDate"
                                :min_date="booking.start_date"
                                class="m-l-10 inline-block align-middle"
                            ></date-picker>

                        </div>

                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-xs-9 col-sm-10">
                    <div class="form-group" :class="{ 'has-error' : errors.has('heading') }">
                        <label for="booking-other-heading" class="control-label">{{ trans('calendar.create_other_label_heading') }}</label>
                        <input type="text" v-model="booking.heading" class="form-control" name="heading" id="booking-other-heading" required/>
                        <span v-show="errors.has('heading')" class="help-block">{{ errors.first('heading') }}</span>
                    </div>
                </div>
                <div class="col-xs-3 col-sm-2">
                    <color-picker
                        :stored_color="booking.color"
                        @changeColor="booking.color = $event"
                    ></color-picker>
                </div>
            </div>

            <!-- Contact select Dropdown -->
            <div v-if="can_see_clients" class="row">
                <div :class="{ 'col-xs-12 col-sm-12': !editMode && can_edit_clients, 'col-xs-12': editMode || !can_edit_clients }">
                    <div class="form-group" :class="{ 'has-error': errors.has('contact_id') }">
                        <!-- todo: contact booking, translations -->
                        <label for="client" class="control-label">
                            Select Contact
                        </label>

                        <clients-select-new
                            id="create-contact-booking-contact-select"
                            v-model="contactId"
                            payers
                            placeholder="Select Contact"
                            :disabled="addingEditBookingData || !hasContacts"
                            dom-ref="contact_select"
                            @clientSelected="selectContact"
                        ></clients-select-new>

                        <span v-show="errors.has('contact_id')" class="help-block">
                            {{ errors.first('contact_id') }}
                        </span>

                        <small v-show="!hasContacts" :class="{ 'help-block': errors.has('contact_id') }">
                            <span v-show="canCreateContact">
                                {{ trans('clients.no_client_selected') }} {{ trans('apps.other_payers.contact_bookings.warning_with_access') }}
                                <a href="/app/settings/apps/other-payers/settings">
                                    {{ trans('settings.title') }} > {{ trans('apps.other_payers.name') }} > {{ trans('apps.other_payers.add_payer') }}
                                </a>
                            </span>
                            <span v-show="!canCreateContact">
                                {{ trans('clients.no_client_selected') }} {{ trans('apps.other_payers.contact_bookings.warning_without_access') }}
                            </span>
                        </small>
                    </div>
                </div>
            </div>

            <!-- Client select Dropdown -->
            <div v-if="booking.contact_id && hasClients" class="row">
                <div class="col-xs-12">
                    <div class="form-group" :class="{ 'has-error': errors.has('clientid') }">
                        <label for="client" class="control-label">
                            {{ trans('calendar.create_client_label_choose_client') }}
                        </label>

                        <clients-select-new
                            id="create-contact-booking-client-select"
                            v-model="clientId"
                            :placeholder="trans('calendar.create_client_label_choose_client')"
                            :payer-id="booking.contact_id"
                            :disabled="addingEditBookingData"
                            @clientSelected="selectClient"
                        ></clients-select-new>

                        <span v-show="errors.has('clientid')" class="help-block">
                            {{ errors.first('clientid') }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Calendar + Practitioner Selects -->
            <div v-if="show_calendars_select || show_practitioners_select" class="row">
                <div
                    v-if="show_calendars_select"
                    class="col-xs-12"
                    :class="{
                                'col-xs-9 col-sm-10 col-sm-6': show_practitioners_select && !minimize_practitioners,
                                'col-xs-9 col-sm-10': show_practitioners_select && minimize_practitioners,
                            }"
                >
                    <div class="form-group" :class="{ 'has-error': errors.has('calendar') }">
                        <label for="calendar" class="control-label">
                            {{ trans('calendar.create_client_label_choose_calendar') }}
                            <i
                                class="zmdi zmdi-circle m-l-5"
                                :style="'color:' + chosen_calendar.color + ';'"
                            ></i>
                        </label>

                        <select
                            id="calendar"
                            v-model="booking.calendarid"
                            class="form-control"
                            name="calendar"
                        >
                            <option v-for="calendar in calendars" :key="calendar.id" :value="calendar.id">
                                {{ calendar.name }}
                            </option>
                        </select>
                        <span v-show="errors.has('calendar')" class="help-block">
                                    {{ errors.first('calendar') }}
                                </span>
                    </div>
                </div>

                <div
                    v-if="show_practitioners_select && minimize_practitioners && show_calendars_select"
                    class="col-sm-2 col-xs-3"
                >
                    <a
                        :title="trans('usermanagement.change_employee')"
                        class="btn btn-default add-client btn-icon pull-right btn-block mt-8 py-4 rounded-md shadow-none"
                        @click.prevent="minimize_practitioners = false"
                    >
                        <i class="zmdi zmdi-account" style="margin-right: 2px;"></i>
                        <i class="zmdi zmdi-refresh-alt"></i>
                    </a>
                </div>

                <div
                    v-if="show_practitioners_select && (!minimize_practitioners || !show_calendars_select)"
                    class="col-xs-12"
                    :class="{ 'col-sm-6': show_calendars_select }"
                >
                    <div class="form-group" :class="{ 'has-error': errors.has('practitioners.0.id') }">
                        <label for="select_employee" class="control-label">
                            {{ $store.getters['practitioners/label'] }}
                        </label>

                        <!-- v-model breaks this element on Safari. Could be fixed in future Safari/Vue releases -->
                        <select
                            id="select_employee"
                            name="select_employee"
                            :value="JSON.stringify(booking.practitioners)"
                            class="form-control"
                            @input="onBookingPractitionersInput"
                        >
                            <option :value="JSON.stringify([])">{{
                                    trans('usermanagement.no_employee_selected')
                                }}
                            </option>

                            <option
                                v-for="practitioner in practitioners"
                                :key="practitioner.id"
                                :value="JSON.stringify([practitioner])"
                            >
                                {{ practitioner.realname }}
                            </option>
                        </select>

                        <span v-show="errors.has('practitioners.0.id')" class="help-block">
                            {{ errors.first('practitioners.0.id') }}
                        </span>
                    </div>
                </div>
            </div>

            <input v-else type="hidden" name="calendar" :value="calendars[0].id" />

            <ep-banner
                v-if="!practitionerCanSeeCalendar"
                id="user-cannot-see-calendar-error"
                variant="warning"
                class="text-sm m-t-0 m-b-20"
            >
                <span>{{ trans('usermanagement.user_cannot_see_booking_calendar') }}</span>
            </ep-banner>

            <div class="row">
                <div class="col-xs-12">
                    <div class="form-group">
                        <label for="notes" class="control-label">
                            {{ trans('calendar.create_client_label_notes') }}
                        </label>

                        <textarea-autosize
                            id="notes"
                            v-model="booking.notes"
                            name="notes"
                            class="form-control"
                            style="min-height:56px;"
                        ></textarea-autosize>

                        <p class="help-block text-muted">
                            {{ trans('calendar.create_client_label_notes_description') }}
                        </p>
                    </div>
                </div>
            </div>

        </form>

        <template #footer>
            <a
                v-if="editMode"
                href=""
                class="btn btn-default m-r-10"
                @click.prevent="showDeleteBooking()"
            >
                <i class="fa fa-trash m-r-10"></i>
                {{ trans('calendar.create_client_label_delete') }}
            </a>
            <button
                type="submit"
                class="btn btn-custom"
                :disabled="loading || !hasContacts"
                @click.prevent="submitForm"
            >
                {{ booking.id ? trans('global.save') : trans('global.add') }}
            </button>
        </template>
    </modal>
</template>
